import {
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  useDisclosure,
  Center,
} from "@chakra-ui/react";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import SecondarySidebar from "../../../components/v2/SecondarySidebar";
import { FORM_VENDORS } from "../../../common/types/form";
import SidebarBackButton from "../../../components/v2/SidebarBackButton";
import urls from "../../../urls";
import SideBar from "../../../components/SideBar";
import ContentContainerWithHeader from "../../../components/v2/ContentContainerWithHeader";
import ContentContainer from "../../../components/v2/ContentContainer";
import IButton from "../../../components/IButton";
import CopyText from "./components/CopyText";
import DisconnectProvider from "./components/DisconnectProvider";
import {
  disconnectFormsProvider,
  listFormProviders,
  selectForm,
} from "./formSlice";
import { useAppDispatch } from "../../../store";
import { useSelector } from "react-redux";
import { isFulfilled, isLoading } from "../../../common/helper/commonHelper";
import SpinnerContainer from "../../../components/SpinnerContainer";
import { STATUS } from "../../../common/types/unifiedMapping";

export default function FormProvider() {
  const {
    providersList: { data: providersList, loading },
    disconnectingProvider,
  } = useSelector(selectForm);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { vendor } = useParams();

  const { isOpen, onOpen, onClose } = useDisclosure();

  const providerDetails = useMemo(
    () =>
      providersList.length
        ? providersList[0]
        : { webhook_url: "", api_key: "", name: "", state: STATUS.DISABLED },
    [providersList]
  );

  const isDisabledProvider = useMemo(
    () => providerDetails.state === STATUS.DISABLED,
    [providerDetails]
  );

  useEffect(() => {
    dispatch(listFormProviders(vendor as FORM_VENDORS));
  }, [dispatch, vendor]);

  async function disconnectProvider() {
    const {
      meta: { requestStatus },
    } = await dispatch(disconnectFormsProvider(providerDetails.api_key));
    isFulfilled(requestStatus) && navigate(urls.unifiedMapping);
  }

  return (
    <>
      <Flex>
        <SecondarySidebar
          heading={providerDetails.name}
          backButton={
            <SidebarBackButton
              onClick={() => navigate(urls.unifiedMapping)}
              text="Back to Data Connections"
            />
          }
        >
          <SideBar
            options={["Connection Details"]}
            selected={0}
            select={(num) => {}}
          />
        </SecondarySidebar>
        <SpinnerContainer loading={isLoading(loading)} h="calc(100vh - 75px)">
          <ContentContainerWithHeader>
            <HStack
              h="65px"
              justifyContent="flex-end"
              py={1}
              alignItems="center"
            >
              {!isDisabledProvider && (
                <IButton
                  variant="solid"
                  colorScheme="red"
                  onClick={onOpen}
                  isLoading={isLoading(disconnectingProvider)}
                >
                  Disconnect
                </IButton>
              )}
            </HStack>
            <ContentContainer
              flexDir="column"
              bg="white"
              maxH="calc(100vh - 60px)"
            >
              {isDisabledProvider ? (
                <Center h="20vh">
                  <Text color="gray.500">Provider is not connected.</Text>
                </Center>
              ) : (
                <VStack alignItems="flex-start" spacing={4} w="500px">
                  <Box w="100%">
                    <Text fontWeight="medium" fontSize="lg">
                      Webhook details
                    </Text>
                    <CopyText
                      textType="webhook"
                      text={providerDetails.webhook_url ?? ""}
                    />
                  </Box>
                  {vendor === FORM_VENDORS.FORMSTACK && (
                    <Box w="100%">
                      <Text fontWeight="medium" fontSize="lg">
                        Shared secret details
                      </Text>
                      <CopyText
                        text={providerDetails.api_key ?? ""}
                        textType="text"
                      />
                    </Box>
                  )}
                </VStack>
              )}
            </ContentContainer>
          </ContentContainerWithHeader>
        </SpinnerContainer>
      </Flex>
      <DisconnectProvider
        isOpen={isOpen}
        onClose={onClose}
        providerName={providerDetails.name}
        onDisconnect={disconnectProvider}
      />
    </>
  );
}
